import { EqlAttributeConfigurationDc, GeometryType, LayerReferenceConfigurationDc } from "@evergis/api";
import React, { FC, useCallback, useMemo } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useBulkOperationsToggle } from "../../../../../hooks/useBulkOperationsToggle";
import { reoderList } from "../../../../../utils/array";
import { AttributesVisibilityHeader } from "../../../DialogComponents/Attributes/AttributesVisibilityHeader";
import { StyledDraggableList } from "../Project/styled";
import { QueryAttributeNew } from "./QueryAttributeNew";
import { QueryLayerAttribute } from "./QueryLayerAttribute";
import { QueryAttributesContainer } from "./styled";
import { EqlAttribute } from "./types";

export interface QueryLayerAttributesProps {
  attributes: EqlAttribute[];
  updateAttribute: (name: string, attributeUpdate: Partial<EqlAttributeConfigurationDc>) => void;
  setAttributes: (attributes: EqlAttributeConfigurationDc[]) => void;
  deleteAttribute: (name: string) => void;
  deleteSysAttribute: (name: string) => void;
  displayField: string;
  idProperty: string;
  geometryProperty: string;
  setDisplayField: (fieldName: string) => void;
  layerReferences?: LayerReferenceConfigurationDc[];
  geometryType?: GeometryType;
  canDelete?: boolean;
  newOpen?: boolean;
  onNewClose?: VoidFunction;
  onNewApply?: (attributeConfig: EqlAttributeConfigurationDc) => void;
}

export const QueryLayerAttributes: FC<QueryLayerAttributesProps> = ({
  attributes,
  updateAttribute,
  setAttributes,
  deleteAttribute,
  deleteSysAttribute,
  displayField,
  idProperty,
  geometryProperty,
  setDisplayField,
  layerReferences,
  geometryType,
  canDelete,
  newOpen,
  onNewClose,
  onNewApply,
}) => {
  const [allAttributes, toggleAllAttributes] = useBulkOperationsToggle<EqlAttributeConfigurationDc>({
    items: attributes,
    setItems: setAttributes,
    column: "isDisplayed",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attributeNames = useMemo(() => attributes.map(({ attributeName }) => attributeName), [attributes.length]);

  const onDragEnd = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination) return;
      const newAttrs = reoderList(attributes, source.index, destination.index);

      setAttributes(newAttrs);
    },
    [attributes, setAttributes],
  );

  return (
    <QueryAttributesContainer>
      <AttributesVisibilityHeader isEdit toggleAllAttributes={toggleAllAttributes} allAttributes={allAttributes} />
      <StyledDraggableList
        onDragEnd={onDragEnd}
        id={"attributes-list"}
        items={attributes as unknown as Record<string, unknown>[]}
        getId={item => (item as unknown as EqlAttributeConfigurationDc)?.attributeName}
      >
        {(attribute, provided) => (
          <QueryLayerAttribute
            draggableProvided={provided}
            attribute={attribute as unknown as EqlAttributeConfigurationDc}
            updateAttribute={updateAttribute}
            deleteAttribute={deleteAttribute}
            deleteSysAttribute={deleteSysAttribute}
            displayField={displayField}
            setDisplayField={setDisplayField}
            layerReferences={layerReferences}
            geometryType={geometryType}
            canDelete={
              canDelete &&
              ![idProperty, geometryProperty].includes(
                (attribute as unknown as EqlAttributeConfigurationDc)?.attributeName,
              )
            }
          />
        )}
      </StyledDraggableList>
      {newOpen && <QueryAttributeNew attributeNames={attributeNames} onClose={onNewClose} onApply={onNewApply} />}
    </QueryAttributesContainer>
  );
};
